import { v4 as uuid } from "uuid";
import { SplitAction, SplitActionFE } from "@common/helpers/rules";
import {
    RuleActionType,
    RuleConditionAmount,
    RuleConditionDate,
    TransferRuleDirection,
    hasExplicitAccrualAccountIds,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
    parseAssignToScheduleActionValue,
} from "@common/types/transactionRule";

export interface TransactionRuleFormSchema {
    groupId: string;
    conditions: TransactionRuleConditionFormSchema[];
    actions: TransactionRuleActionFormSchema[];
    note: string;
    applyToExistingTransactions: boolean;
    excludedTransactionsIds: number[];
    includedTransactionsIds: number[];
    excludeSplitTransactions?: boolean;
}

export enum FormRuleConditionType {
    CATEGORY = "Category",
    SUGGESTED_CATEGORY = "Suggested category",
    AMOUNT = "Amount",
    BANK_DESCRIPTION = "Bank description",
    ACCOUNT = "Account",
    ENTITY = "Entity",
    COUNTERPARTY = "Vendor",
    DATE = "Date",
    DATE_PROXIMITY = "Date proximity",
    CLASS = "Class",
    SIMILAR_TO = "Similar to",
}

export enum DateConditionType {
    BEFORE = "Before",
    AFTER = "After",
    BETWEEN = "Between",
}

export enum AmountConditionType {
    GREATER_THAN = "Is above",
    LESS_THAN = "Is below",
    BETWEEN = "Is between",
    IS = "Is",
    IS_ROUND = "Is round",
    IS_NOT_ROUND = "Is not round",
}

export enum DescriptionConditionType {
    CONTAINS = "Contains",
    IS = "Is",
    DOES_NOT_CONTAIN = "Does not contain",
}

export enum AmountRoundingConditionType {
    DOLLAR = "To the dollar",
    TEN = "To the nearest $10",
    HUNDRED = "To the nearest $100",
    THOUSAND = "To the nearest $1,000",
}

export interface TransactionRuleConditionFormSchema {
    id: string;
    conditionType?: FormRuleConditionType;
    amountIsCredit: boolean;
    amountConditionType: AmountConditionType;
    dateConditionType: DateConditionType;
    descriptionConditionType: DescriptionConditionType;
    amountSingleValue?: number;
    amountValueRange?: RuleConditionAmount;
    dateValueRange?: RuleConditionDate;
    descriptionValue?: string[];
    categoryIds?: string[];
    classIds?: string[];
    accountValue?: number[];
    subaccountValue?: string[];
    entityValue?: number[];
    counterpartyIds?: string[];
    transferDirection: TransferRuleDirection | null;
    dateProximity?: number;
    normalizedDescriptionId?: string;
    // D2: undefined = legacy (whole cluster); null = plain rows only; string = split type.
    // Kept on the form row so the SIMILAR_TO split guard survives the model→form→model
    // round trip when a user edits and re-saves a drawer-created rule.
    effectiveSplitKey?: string | null;
    amountRoundingLevel?: AmountRoundingConditionType;
}

export enum FormRuleActionType {
    CATEGORY = "Update category",
    ENTITY = "Move to entity",
    ACCOUNT_ID = "Assign to cash ledger",
    ACCOUNT_NAME = "Assign to cash ledger by name",
    ACCRUAL_ACCOUNT_ID = "Assign to accrual ledger",
    CLASS = "Set class",
    MEMO = "Add memo",
    COUNTERPARTY = "Assign counterparty",
    CUSTOMER = "Assign billable customer",
    SPLIT = "Split",
    ASSIGN_TO_SCHEDULE = "Set recognition schedule",
}

export const BusinessRuleFormActions = [
    FormRuleActionType.CATEGORY,
    FormRuleActionType.ENTITY,
    FormRuleActionType.ACCOUNT_ID,
    FormRuleActionType.ACCRUAL_ACCOUNT_ID,
    FormRuleActionType.MEMO,
    FormRuleActionType.COUNTERPARTY,
    FormRuleActionType.CUSTOMER,
    FormRuleActionType.CLASS,
    FormRuleActionType.ASSIGN_TO_SCHEDULE,
];

export const AccountingRuleFormActions = [
    FormRuleActionType.ACCOUNT_ID,
    FormRuleActionType.ACCRUAL_ACCOUNT_ID,
];

// Map a frontend FormRuleActionType back to its backend RuleActionType. Used to
// send the draft rule's action TYPES to the matching-transactions preview so the
// backend can compute lock-safety (the form stores display-string enum values,
// which the BE schema does not accept directly).
export const mapFormActionTypeToRuleActionType = (
    type: FormRuleActionType,
): RuleActionType => {
    switch (type) {
        case FormRuleActionType.CATEGORY:
            return RuleActionType.CATEGORY;
        case FormRuleActionType.ENTITY:
            return RuleActionType.ENTITY;
        case FormRuleActionType.ACCOUNT_ID:
            return RuleActionType.ACCOUNT_ID;
        case FormRuleActionType.ACCOUNT_NAME:
            return RuleActionType.ACCOUNT_NAME;
        case FormRuleActionType.ACCRUAL_ACCOUNT_ID:
            return RuleActionType.ACCRUAL_ACCOUNT_ID;
        case FormRuleActionType.CLASS:
            return RuleActionType.CLASS;
        case FormRuleActionType.MEMO:
            return RuleActionType.MEMO;
        case FormRuleActionType.COUNTERPARTY:
            return RuleActionType.COUNTERPARTY;
        case FormRuleActionType.CUSTOMER:
            return RuleActionType.CUSTOMER;
        case FormRuleActionType.SPLIT:
            return RuleActionType.SPLIT;
        case FormRuleActionType.ASSIGN_TO_SCHEDULE:
            return RuleActionType.ASSIGN_TO_SCHEDULE;
        default:
            throw new Error(`Unknown form action type: ${type}`);
    }
};

// Map between backend RuleActionType and frontend FormRuleActionType
export const mapRuleActionTypeToForm = (
    type: RuleActionType,
): FormRuleActionType => {
    switch (type) {
        case RuleActionType.CATEGORY:
            return FormRuleActionType.CATEGORY;
        case RuleActionType.ENTITY:
            return FormRuleActionType.ENTITY;
        case RuleActionType.ACCOUNT_ID:
        case RuleActionType.ACCOUNT_NAME:
            return FormRuleActionType.ACCOUNT_ID;
        case RuleActionType.ACCRUAL_ACCOUNT_ID:
        case RuleActionType.ACCRUAL_ACCOUNT_NAME:
            return FormRuleActionType.ACCRUAL_ACCOUNT_ID;
        case RuleActionType.CLASS:
            return FormRuleActionType.CLASS;
        case RuleActionType.MEMO:
            return FormRuleActionType.MEMO;
        case RuleActionType.COUNTERPARTY:
            return FormRuleActionType.COUNTERPARTY;
        case RuleActionType.CUSTOMER:
            return FormRuleActionType.CUSTOMER;
        case RuleActionType.SPLIT:
            return FormRuleActionType.SPLIT;
        case RuleActionType.ASSIGN_TO_SCHEDULE:
            return FormRuleActionType.ASSIGN_TO_SCHEDULE;
        default:
            throw new Error(`Unknown action type: ${type}`);
    }
};

export const mapRuleActionTypeToFormValue = (
    type: RuleActionType,
    value: string | number | string[],
): TransactionRuleActionFormValue => {
    switch (type) {
        case RuleActionType.CATEGORY:
            return {
                categoryId: value as string,
            };
        case RuleActionType.ENTITY:
            return {
                entityId: value as number,
            };
        case RuleActionType.ACCOUNT_ID: {
            // Always parse both scalar and array so a stored multi-business
            // array survives the round trip regardless of the flag state. The
            // stored shape decides, not the length: deleting an account can
            // leave one uuid on a rule that still covers several businesses,
            // and re-submitting that as a scalar would be rejected on save.
            const accountIds = parseAccountIdActionValue(value);
            if (accountIds && Array.isArray(value)) {
                return { accountIds };
            }
            // The parser is uuid-strict; a legacy non-uuid scalar must still
            // round-trip verbatim instead of dropping the action on save.
            return {
                accountId:
                    accountIds?.[0] ??
                    (typeof value === "string" ? value : undefined),
            };
        }
        case RuleActionType.ACCOUNT_NAME:
            return {
                accountName: value as string,
            };
        case RuleActionType.ACCRUAL_ACCOUNT_ID: {
            const parsed = parseAccrualAccountActionValue(value);
            if (parsed.accountIds && hasExplicitAccrualAccountIds(value)) {
                return {
                    accountIds: parsed.accountIds,
                    autoConfirm: parsed.autoConfirm,
                };
            }
            return {
                accountId: parsed.accountId,
                autoConfirm: parsed.autoConfirm,
            };
        }
        case RuleActionType.ACCRUAL_ACCOUNT_NAME: {
            const parsed = parseAccrualAccountActionValue(value);
            return {
                accountName: parsed.accountName,
                autoConfirm: parsed.autoConfirm,
            };
        }
        case RuleActionType.CLASS:
            return {
                classIds: value as string[],
            };
        case RuleActionType.MEMO:
            return {
                memo: value as string,
            };
        case RuleActionType.COUNTERPARTY:
            return {
                counterpartyId: value as string,
            };
        case RuleActionType.CUSTOMER:
            return {
                customerId: value as string,
            };
        case RuleActionType.ASSIGN_TO_SCHEDULE: {
            const parsed = parseAssignToScheduleActionValue(value);
            return {
                scheduleTemplateId: parsed?.scheduleTemplateId,
                autoPostOnApply: parsed?.autoPostOnApply,
                autoPostRecognition: parsed?.autoPostRecognition,
            };
        }
        case RuleActionType.SPLIT: {
            const parsedValue = JSON.parse(value as string) as SplitAction;
            return {
                splitAction: {
                    ...parsedValue,
                    splits: parsedValue.splits.map((split) => ({
                        ...split,
                        ...(parsedValue.isPercentage
                            ? { percentageAmount: split.amount ?? null }
                            : { amount: split.amount ?? null }),
                    })),
                },
            };
        }
        default:
            throw new Error(`Unknown action type: ${type}`);
    }
};

interface TransactionRuleActionFormValue {
    categoryId?: string;
    entityId?: number;
    accountId?: string;
    accountName?: string;
    // One account uuid per covered business (multi-entity rules). Written by
    // the flag-on picker and preserved through the round trip; supersedes the
    // single-uuid `accountId` when present.
    accountIds?: string[];
    autoConfirm?: boolean;
    classIds?: string[];
    taxonomy?: string[];
    memo?: string;
    counterpartyId?: string;
    customerId?: string;
    scheduleTemplateId?: string;
    autoPostOnApply?: boolean;
    autoPostRecognition?: boolean;
    splitAction?: SplitActionFE;
}

export interface TransactionRuleActionFormSchema {
    id: string;
    actionType?: FormRuleActionType;
    value?: TransactionRuleActionFormValue;
    transferDirection: TransferRuleDirection | null;
}

export function createDefaultSplitAction(): TransactionRuleActionFormSchema {
    return {
        id: uuid(),
        actionType: FormRuleActionType.SPLIT,
        value: {
            splitAction: {
                splits: [{ percentageAmount: 50 }, { percentageAmount: 50 }],
                isPercentage: true,
            },
        },
        transferDirection: null,
    };
}
